// 能力投影的文案查表（SPEC #public-api C9：字串表的 key 是 label_id；VMS UX 評審 2026-10-13 B-22、P1-12）。翻譯表放 i18next 的
// kinio_model namespace，來源是 kinio_ui_strings.json（app/home-base 三平台共用那份的照抄，位元組相同；key：label_id、
// <key>.help、<key>.confirm；en / ja / zh-Hans），查不到用投影帶來的 zh-Hant 原文；不以 zh-Hant 原文當翻譯 key。
import i18n from 'i18next';
import type { SettingItem, SettingPreset } from './deviceApi';
import catalogLabels from './kinio_ui_labels.json';

export const KINIO_MODEL_NS = 'kinio_model';

/** label_id 有翻譯就用翻譯，否則用 fallback（投影的 zh-Hant 原文）。label_id 含點號，不當巢狀 key。 */
export function kinioLabel(labelId: string | undefined, fallback: string): string {
  if (!labelId) return fallback;
  return i18n.t(labelId, { ns: KINIO_MODEL_NS, keySeparator: false, nsSeparator: false, defaultValue: fallback });
}

/**
 * 設備目錄的 zh-Hant 名稱：VMS 照 protocol main 重抽的 kinio_ui_labels.json（attrs / commands，key = label_id）。
 * 平台投影帶的 label 是平台 vendor 的 protocol 版本，可能比較舊（第十三輪 B13-03：投影還寫「TTL 橋接」，protocol 0afa886 已改「UART（TTL）橋接」）；
 * 這裡有就用這裡的（稽核的命令名也是這一份，兩處同名），沒有才用投影的。
 */
const CATALOG = catalogLabels as { attrs: Record<string, string>; commands: Record<string, string> };
export const catalogLabel = (labelId: string | undefined): string | undefined =>
  labelId ? CATALOG.attrs[labelId] ?? CATALOG.commands[labelId] : undefined;

/**
 * 畫面上要換掉的條目名（投影的名稱不好懂、設備目錄還沒改之前；第二輪 R2-P1-5「通道摘要」→「通道」）。
 * Kinio Node 的 ttl_enabled / ttl_baud / ttl_dut 已由設備目錄改名（protocol 0afa886）＋字串表（home-base 28f394a4）涵蓋，不再換；
 * ttl_token_set / ttl_rx_at 字串表還沒有 en / ja / zh-Hans 譯文，先留畫面端字串（不然英文畫面會露中文）。
 */
const LABEL_OVERRIDE: Record<string, string> = {
  'kinio_system.channel_online_summary': 'dset.ch_summary_label',
  'kinio_node.ttl_token_set': 'dset.ttl_token_set_label',
  'kinio_node.ttl_rx_at': 'dset.ttl_rx_at_label',
};

export const itemLabel = (it: Pick<SettingItem, 'label' | 'label_id'>): string =>
  it.label_id && LABEL_OVERRIDE[it.label_id] ? i18n.t(LABEL_OVERRIDE[it.label_id]) : kinioLabel(it.label_id, catalogLabel(it.label_id) ?? it.label);

/** enum 值的顯示文字：enum_label_ids 查表 → enum_labels → enum 名稱 → 值本身。 */
export function enumText(it: Pick<SettingItem, 'enum' | 'enum_labels' | 'enum_label_ids'>, v: string | number): string {
  const k = String(v);
  return kinioLabel(it.enum_label_ids?.[k], it.enum_labels?.[k] ?? it.enum?.[k] ?? k);
}

export const presetText = (p: SettingPreset): string => kinioLabel(p.label_id, p.label);
